import { Outlet, Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SITE_NAME } from "@/constants/site";

const TABS = [
  { to: "/admin/features", label: "Features" },
  { to: "/admin/categories", label: "Categories" },
  { to: "/admin/industries", label: "Industries" },
  { to: "/admin/tags", label: "Tags" },
  { to: "/admin/settings", label: "Site Settings" },
  { to: "/admin/leads", label: "Leads" },
  { to: "/admin/pricing", label: "Pricing" },
  { to: "/admin/users", label: "Users" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <p className="font-mono-ui text-xs uppercase tracking-widest text-neutral-500">
          Loading…
        </p>
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900" data-testid="admin-layout">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/bondstein-logo.png" alt="Bondstein" className="h-7 w-auto object-contain" />
            <div className="flex items-center gap-2">
              <span className="font-display font-bold tracking-tight text-neutral-950 text-sm">
                Bondstein CMS
              </span>
              <span className="hidden lg:inline font-mono-ui text-[9px] uppercase tracking-[0.12em] text-neutral-500 ml-1 border-l border-neutral-300 pl-2 max-w-[180px] line-clamp-2 leading-snug">
                {SITE_NAME}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden sm:inline text-xs font-medium text-neutral-600 hover:text-neutral-950"
            >
              View site ↗
            </Link>
            <span className="text-xs text-neutral-500 hidden md:inline">{user.email}</span>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              className="text-xs font-semibold text-neutral-700 hover:text-red-600 border border-neutral-200 px-3 py-1.5 rounded-md"
            >
              Sign out
            </button>
          </div>
        </div>
        <nav className="border-t border-neutral-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto">
            {TABS.map((t) => {
              const active = location.pathname === t.to || location.pathname.startsWith(t.to + "/");
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`admin-tab-${t.label.toLowerCase()}`}
                  className={`px-3 py-2.5 text-sm border-b-2 transition-colors ${
                    active
                      ? "border-[#84CC16] text-neutral-950 font-semibold"
                      : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
